import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, RefreshCw } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useCompany } from "../context/CompanyContext";
import { Money, PageHeader, TableSkeleton } from "../components/erp";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Card, CardContent } from "../components/ui/card";

const Table = ({ head, children, testId }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm" data-testid={testId}>
      <thead><tr className="bg-slate-50 dark:bg-slate-800/60 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
        {head.map((h) => <th key={h} className="p-3">{h}</th>)}</tr></thead>
      <tbody>{children}</tbody>
    </table>
  </div>
);

const REPORT_URLS = {
  gst: "/reports/gst-summary", stock: "/reports/stock-register",
  pl: "/reports/profit-loss", ageing: "/reports/receivables-ageing",
};
const EXPORT_TYPES = { gst: "gst_hsn", stock: "stock", pl: "sales", ageing: "receivables" };

function GstReport({ data }) {
  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 p-4" data-testid="gst-cards">
        {[["Output Tax (Sales)", data.output.tax, "text-red-600"], ["Input Tax (Purchases)", data.input.tax, "text-emerald-600"],
          ["Net GST Liability", data.net_liability, "text-amber-600"], ["Taxable Sales", data.output.taxable, ""]].map(([k, v, c]) => (
          <Card key={k} className="border-slate-200/80 dark:border-slate-800">
            <CardContent className="p-4">
              <p className="text-[11px] uppercase tracking-wider text-slate-500">{k}</p>
              <Money value={v} className={`block mt-1 text-lg font-bold ${c}`} />
            </CardContent>
          </Card>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-slate-100 dark:bg-slate-800">
        {[["Output GST (Sales)", data.output], ["Input GST (Purchases)", data.input]].map(([title, d]) => (
          <div key={title} className="bg-white dark:bg-slate-900 p-4">
            <p className="font-semibold text-sm mb-2">{title}</p>
            <p className="text-xs text-slate-500 mb-3">{d.count} documents</p>
            {[["Taxable", d.taxable], ["CGST", d.cgst], ["SGST", d.sgst], ["IGST", d.igst]].map(([k, v]) => (
              <div key={k} className="flex justify-between py-1 text-sm border-b border-slate-50 dark:border-slate-800">
                <span className="text-slate-500">{k}</span><Money value={v} />
              </div>
            ))}
          </div>
        ))}
      </div>
      <div className="p-4 border-t border-slate-100 dark:border-slate-800">
        <p className="font-semibold text-sm mb-2">HSN / SAC Summary (Sales)</p>
        <Table head={["HSN/SAC", "Qty", "Taxable", "Tax"]} testId="hsn-table">
          {data.hsn.map((h) => (
            <tr key={h.hsn} className="border-b border-slate-100 dark:border-slate-800">
              <td className="p-3 font-mono text-xs">{h.hsn}</td><td className="p-3 font-mono">{h.qty}</td>
              <td className="p-3"><Money value={h.taxable} /></td><td className="p-3"><Money value={h.tax} /></td>
            </tr>
          ))}
          {data.hsn.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-sm text-slate-400">No sales in this period</td></tr>}
        </Table>
      </div>
    </>
  );
}

function StockReport({ data }) {
  return (
    <Table head={["Item", "Code", "Unit", "Opening", "In", "Out", "Closing"]} testId="stock-register-table">
      {data.items.map((r) => (
        <tr key={r.item_id} className="border-b border-slate-100 dark:border-slate-800">
          <td className="p-3 font-medium">{r.name}</td>
          <td className="p-3 font-mono text-xs text-slate-500">{r.code}</td>
          <td className="p-3 text-slate-500">{r.unit}</td>
          <td className="p-3 font-mono">{r.opening}</td>
          <td className="p-3 font-mono text-emerald-600">+{r.in}</td>
          <td className="p-3 font-mono text-red-600">-{r.out}</td>
          <td className="p-3 font-mono font-semibold">{r.closing}</td>
        </tr>
      ))}
      {data.items.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-sm text-slate-400">No items</td></tr>}
    </Table>
  );
}

function PlReport({ data }) {
  return (
    <div className="max-w-md p-5 space-y-2" data-testid="pl-table">
      {[["Revenue (taxable sales)", data.revenue, ""], ["Less: Purchases", -data.purchases, ""],
        ["Gross Profit", data.gross_profit, "font-semibold border-t border-slate-200 dark:border-slate-700 pt-2"],
        ["Less: Expenses (approved)", -data.expenses, ""],
        ["Net Profit", data.net_profit, `font-bold text-lg border-t border-slate-200 dark:border-slate-700 pt-2 ${data.net_profit >= 0 ? "text-emerald-600" : "text-red-600"}`],
      ].map(([k, v, c]) => (
        <div key={k} className={`flex justify-between text-sm ${c}`}>
          <span>{k}</span><Money value={v} />
        </div>
      ))}
      <p className="text-xs text-slate-400 pt-2">Output GST collected: <Money value={data.output_tax} /> (remitted to government, not income)</p>
    </div>
  );
}

function AgeingReport({ data }) {
  return (
    <>
      <div className="flex flex-wrap gap-2 p-4" data-testid="ageing-buckets">
        {Object.entries(data.buckets).map(([k, v]) => (
          <span key={k} className="rounded-lg border border-slate-200 dark:border-slate-800 px-3 py-1.5 text-xs">
            <span className="text-slate-500">{k === "not_due" ? "Not due" : `${k} days`}:</span> <Money value={v} className="font-semibold" />
          </span>
        ))}
      </div>
      <Table head={["Invoice", "Customer", "Due Date", "Days Overdue", "Balance"]} testId="ageing-table">
        {data.items.map((r) => (
          <tr key={r.invoice_no} className="border-b border-slate-100 dark:border-slate-800">
            <td className="p-3 font-mono text-xs">{r.invoice_no}</td>
            <td className="p-3">{r.party_name}</td>
            <td className="p-3">{r.due_date || "—"}</td>
            <td className="p-3 font-mono">{r.days > 0 ? `${r.days}d` : "—"}</td>
            <td className="p-3"><Money value={r.balance} className="font-semibold text-red-600" /></td>
          </tr>
        ))}
        {data.items.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-sm text-slate-400">No outstanding invoices</td></tr>}
      </Table>
    </>
  );
}

export default function Reports() {
  const { activeId } = useCompany();
  const [tab, setTab] = useState("gst");
  const [frm, setFrm] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState(null);
  const [loadedTab, setLoadedTab] = useState(null);

  const load = () => {
    const qs = `${frm ? `?frm=${frm}${to ? `&to=${to}` : ""}` : to ? `?to=${to}` : ""}`;
    const target = tab;
    setData(null);
    setLoadedTab(null);
    api.get(`${REPORT_URLS[target]}${qs}`)
      .then((r) => { setData(r.data); setLoadedTab(target); })
      .catch((e) => toast.error(errMsg(e)));
  };
  useEffect(load, [activeId, tab]); // eslint-disable-line

  const exportCsv = async () => {
    try {
      const qs = `${frm ? `&frm=${frm}` : ""}${to ? `&to=${to}` : ""}`;
      const r = await api.get(`/reports/export?type=${EXPORT_TYPES[tab]}${qs}`, { responseType: "blob" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      link.download = `${EXPORT_TYPES[tab]}_report.csv`;
      link.click();
      toast.success("CSV downloaded");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="reports-page">
      <PageHeader title="Reports Center" testId="reports-title" sub="GST, stock, P&L and ageing — with CSV export" />

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div><Label className="text-xs">From</Label><Input data-testid="report-from" type="date" className="mt-1 w-40" value={frm} onChange={(e) => setFrm(e.target.value)} /></div>
        <div><Label className="text-xs">To</Label><Input data-testid="report-to" type="date" className="mt-1 w-40" value={to} onChange={(e) => setTo(e.target.value)} /></div>
        <Button data-testid="report-apply" variant="outline" onClick={load}><RefreshCw className="mr-1.5 h-4 w-4" /> Apply</Button>
        <Button data-testid="report-export" variant="outline" onClick={exportCsv}>
          <Download className="mr-1.5 h-4 w-4" /> Export CSV
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4 flex-wrap h-auto">
          <TabsTrigger value="gst" data-testid="report-tab-gst">GST Summary</TabsTrigger>
          <TabsTrigger value="stock" data-testid="report-tab-stock">Stock Register</TabsTrigger>
          <TabsTrigger value="pl" data-testid="report-tab-pl">Profit & Loss</TabsTrigger>
          <TabsTrigger value="ageing" data-testid="report-tab-ageing">Receivables Ageing</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
        {!data || loadedTab !== tab ? <TableSkeleton />
          : tab === "gst" ? <GstReport data={data} />
          : tab === "stock" ? <StockReport data={data} />
          : tab === "pl" ? <PlReport data={data} />
          : <AgeingReport data={data} />}
      </div>
    </div>
  );
}
